import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  defaultBrpConfidenceFilter,
  defaultBrpVerdictFilter,
  defaultReviewStatusFilter,
  defaultServiceFilter,
  defaultSourcesFilter,
  defaultStatusFilter,
} from '../../utils';

type LabelOption = { label: string };
type LabelValueOption = { label: string; value: string };
type CompanyOption = { label: string; id: number };

export interface FilterState {
  companysExclude: CompanyOption[];
  protectedAssetsInclude: string[];
  protectedAssetsExclude: string[];
  getPickerStartDate: string | string[];
  companyFilterData: CompanyOption[];
  getPickerEndDate: string | string[];
  statusFilterData: LabelOption[];
  getServicesData: LabelOption[];
  getSourcesData: LabelValueOption[];
  reviewStatusFilterData: LabelOption[];
  brpVerdictFilterData: LabelValueOption[];
  brpConfidenceFilterData: LabelValueOption[];
  datePickerPersistValues: { startDate: string; endDate: string };
}

// This slice is persisted (redux-persist whitelist key 'filterList') - the
// state shape must stay identical so existing persisted filters rehydrate.
const initialState: FilterState = {
  companysExclude: [],
  protectedAssetsInclude: [],
  protectedAssetsExclude: [],
  getPickerStartDate: [],
  companyFilterData: [],
  getPickerEndDate: [],
  statusFilterData: defaultStatusFilter,
  getServicesData: defaultServiceFilter,
  getSourcesData: defaultSourcesFilter,
  reviewStatusFilterData: defaultReviewStatusFilter,
  brpVerdictFilterData: defaultBrpVerdictFilter,
  brpConfidenceFilterData: defaultBrpConfidenceFilter,
  datePickerPersistValues: { startDate: '', endDate: '' },
};

const filterSlice = createSlice({
  name: 'filterList',
  initialState,
  reducers: {
    setStatusFilterValues: (state, action: PayloadAction<LabelOption[]>) => {
      state.statusFilterData = action.payload;
    },
    setServicesData: (state, action: PayloadAction<LabelOption[]>) => {
      state.getServicesData = action.payload;
    },
    setSourcesData: (state, action: PayloadAction<LabelValueOption[]>) => {
      state.getSourcesData = action.payload;
    },
    setPickerStartDate: (state, action: PayloadAction<string | string[]>) => {
      state.getPickerStartDate = action.payload;
    },
    setPickerEndDate: (state, action: PayloadAction<string | string[]>) => {
      state.getPickerEndDate = action.payload;
    },
    setCompanyFilter: (state, action: PayloadAction<CompanyOption[]>) => {
      state.companyFilterData = action.payload;
    },
    setExcludeCompany: (state, action: PayloadAction<CompanyOption[]>) => {
      state.companysExclude = action.payload;
    },
    setProtectedAssetsInclude: (state, action: PayloadAction<string[]>) => {
      state.protectedAssetsInclude = action.payload;
    },
    setProtectedAssetsExclude: (state, action: PayloadAction<string[]>) => {
      state.protectedAssetsExclude = action.payload;
    },
    setReviewStatusFilterValues: (
      state,
      action: PayloadAction<LabelOption[]>,
    ) => {
      state.reviewStatusFilterData = action.payload;
    },
    setDatePickerPersistValues: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>,
    ) => {
      state.datePickerPersistValues = action.payload;
    },
    setBrpVerdictFilter: (state, action: PayloadAction<LabelValueOption[]>) => {
      state.brpVerdictFilterData = action.payload;
    },
    setBrpConfidenceFilter: (
      state,
      action: PayloadAction<LabelValueOption[]>,
    ) => {
      state.brpConfidenceFilterData = action.payload;
    },
  },
});

export const {
  setStatusFilterValues,
  setServicesData,
  setSourcesData,
  setPickerStartDate,
  setPickerEndDate,
  setCompanyFilter,
  setExcludeCompany,
  setProtectedAssetsInclude,
  setProtectedAssetsExclude,
  setReviewStatusFilterValues,
  setDatePickerPersistValues,
  setBrpVerdictFilter,
  setBrpConfidenceFilter,
} = filterSlice.actions;
export default filterSlice.reducer;
